import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customActionsApi } from '../services/customActionsApi';
import type {
  CreateCustomActionRequest,
  UpdateCustomActionRequest,
  CustomActionQuery,
} from '@troia-v3/types';

/**
 * Query key factory for custom actions
 */
const customActionsKeys = {
  all: ['custom-actions'] as const,
  lists: () => [...customActionsKeys.all, 'list'] as const,
  list: (query?: CustomActionQuery) => [...customActionsKeys.lists(), query] as const,
  details: () => [...customActionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...customActionsKeys.details(), id] as const,
  logs: (customActionId: string, query?: Record<string, unknown>) =>
    [...customActionsKeys.all, 'logs', customActionId, query] as const,
  logStats: (customActionId: string) =>
    [...customActionsKeys.all, 'log-stats', customActionId] as const,
};

/**
 * Hook to fetch all custom actions with pagination
 */
export const useCustomActions = (query?: CustomActionQuery) => {
  return useQuery({
    queryKey: customActionsKeys.list(query),
    queryFn: () => customActionsApi.getAll(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch single custom action by ID
 */
export const useCustomAction = (id: string) => {
  return useQuery({
    queryKey: customActionsKeys.detail(id),
    queryFn: () => customActionsApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create new custom action
 */
export const useCreateCustomAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomActionRequest) => customActionsApi.create(data),
    onSuccess: () => {
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: customActionsKeys.lists() });
    },
  });
};

/**
 * Hook to update custom action
 */
export const useUpdateCustomAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomActionRequest }) =>
      customActionsApi.update(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific detail and all lists
      queryClient.invalidateQueries({ queryKey: customActionsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: customActionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete custom action
 */
export const useDeleteCustomAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customActionsApi.delete(id),
    onSuccess: () => {
      // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: customActionsKeys.lists() });
    },
  });
};

/**
 * Hook to update custom action status
 */
export const useUpdateCustomActionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      customActionsApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      // Invalidate specific detail and all lists
      queryClient.invalidateQueries({ queryKey: customActionsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: customActionsKeys.lists() });
    },
  });
};

/**
 * Hook to test custom action execution
 */
export const useTestCustomAction = () => {
  return useMutation({
    mutationFn: ({ id, parameters }: { id: string; parameters: Record<string, any> }) =>
      customActionsApi.test(id, parameters),
  });
};

/**
 * Hook to fetch execution logs for a custom action
 */
export const useCustomActionLogs = (
  customActionId: string,
  query?: Record<string, unknown>
) => {
  return useQuery({
    queryKey: customActionsKeys.logs(customActionId, query),
    queryFn: () => customActionsApi.getLogs(customActionId, query),
    enabled: !!customActionId,
    staleTime: 30 * 1000, // 30 seconds - logs change frequently
  });
};

/**
 * Hook to fetch execution stats for a custom action
 */
export const useCustomActionLogStats = (customActionId: string) => {
  return useQuery({
    queryKey: customActionsKeys.logStats(customActionId),
    queryFn: () => customActionsApi.getLogStats(customActionId),
    enabled: !!customActionId,
    staleTime: 30 * 1000,
  });
};
